import React, { ReactNode, useMemo, useState } from 'react'
import { Divider, Flex, Progress, Typography } from 'antd'
import {
  ProgressSection,
  StatsCard,
  ProgressRow,
  ProgressLabel,
  ProgressValue,
  TaskHeader,
  Content,
  TaskData,
  TaskDates,
  DownloadBtn,
} from './styled'
import { SectionCard } from '@/shared/ui/layout/styled'
import {
  PrimaryButton,
  DangerOutlineButton,
  DarkButton,
} from '@/shared/ui/components/Button'
import { Icon } from '@/shared/ui/Icon/Icon'
import { TaskLogs } from './ui/TaskLogs'
import { ErrorAlert } from '@/shared/ui/components/Alert'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'

import {
  STATS_DISPLAY_CONFIG,
  RECONFIGURATION_STATS_DISPLAY_CONFIG,
} from '../../lib/taskTypes'
import {
  TaskItem,
  getDefaultStats,
  TASK_STATUSES,
} from '@/entities/task/model/taskTypes'
import { getStatusTag } from '../../lib/getStatusTag'
import { SYSTEM_TYPE } from '@/app/store/systemStore'
import { ReconfigurationModal } from '../ReconfigurationModal'

const { Text, Title } = Typography

export interface TaskCardProps {
  task: TaskItem
  showLogs: boolean
  isDeleting?: boolean
  isModalView?: boolean
  onToggleLogs: (taskId: string) => void
  onDelete: (taskId: string) => void
  onDownloadCredentials: (taskId: string) => void
  headerButtons?: ReactNode
}

const renderProgressRow = (
  label: string,
  stats: {
    total: number
    processed: number
    percent: number
    displayText: string
  }
) => (
  <ProgressRow key={label} gap={8} align="center">
    <ProgressLabel type="secondary">{label}:</ProgressLabel>
    <Progress percent={stats.percent} size="small" showInfo={false} />
    <ProgressValue strong>{stats.displayText}</ProgressValue>
  </ProgressRow>
)

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  showLogs,
  onToggleLogs,
  onDelete,
  onDownloadCredentials,
  isDeleting,
  isModalView = false,
  headerButtons,
}) => {
  const { isMd, isSm, isXs } = useBreakpoint()
  const [isReconfigModalOpen, setIsReconfigModalOpen] = useState(false)

  const handleReconfigClick = () => {
    setIsReconfigModalOpen(true)
  }

  const handleCloseReconfigModal = () => {
    setIsReconfigModalOpen(false)
  }

  const isTablet = isXs || isSm || isMd
  const isMobile = isXs || isSm

  const safeStats = task.stats || getDefaultStats()

  const isOmnicomm = task.system.toLowerCase() === SYSTEM_TYPE.OMNICOMM

  const succesedTask = task.status === TASK_STATUSES.SUCCESSED

  const hasVehicles = (task.stats?.Vehicles?.total || 0) > 0
  const hasDeviceRedirector = (task.stats?.DeviceRedirector?.total || 0) > 0
  const showReconfigButton = succesedTask && hasVehicles && !isModalView
  const isReconfigurationTask = task.isReconfiguration === true

  const displayConfig = useMemo(() => {
    if (isReconfigurationTask) {
      return RECONFIGURATION_STATS_DISPLAY_CONFIG
    }

    let config = [...STATS_DISPLAY_CONFIG]

    if (isOmnicomm) {
      config = config.filter((item) => item.key !== 'Retranslations')
    }

    return config
  }, [isOmnicomm, hasDeviceRedirector])

  return (
    <>
      <SectionCard $isModalView={isModalView}>
        <TaskHeader
          gap={24}
          vertical={isTablet}
          align={isTablet ? 'inherit' : 'center'}
          justify={!isModalView ? 'space-between' : 'start'}
        >
          <Flex
            align="center"
            gap={24}
            justify={!isModalView && isTablet ? 'space-between' : 'start'}
          >
            <Title level={2}>Задача №{task.number}</Title>
            {getStatusTag(task.status)}
          </Flex>
          {headerButtons || (
            <Flex gap={12} justify={!isModalView && isTablet ? 'end' : 'start'}>
              {!isModalView && (
                <PrimaryButton
                  icon={<Icon name="logs_btn" size={16} fill="white" />}
                  onClick={() => onToggleLogs(task.id)}
                >
                  {showLogs ? 'Скрыть логи' : 'Показать логи'}
                </PrimaryButton>
              )}
              {showReconfigButton && (
                <DarkButton
                  icon={<Icon name="reconfig" size={16} fill="white" />}
                  onClick={handleReconfigClick}
                  style={isMobile ? { width: '40px' } : {}}
                >
                  {!isMobile && 'Перенастройка'}
                </DarkButton>
              )}
              {task.status !== TASK_STATUSES.DELETED && (
                <DangerOutlineButton
                  icon={!isDeleting && <Icon size={16} name="delete_btn" />}
                  onClick={() => onDelete(task.id)}
                  loading={isDeleting}
                  disabled={isDeleting}
                  style={{
                    ...(isDeleting && {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#d9d9d9',
                      color: '#8c8c8c',
                    }),
                    ...(isMobile && { width: '40px' }),
                  }}
                >
                  {!isMobile && (isDeleting ? 'Удаляем...' : 'Удалить')}
                </DangerOutlineButton>
              )}
            </Flex>
          )}
        </TaskHeader>

        <Content gap={24} vertical={isTablet} align="start">
          <TaskData vertical gap={24}>
            <TaskDates vertical={isTablet} wrap gap={isMobile ? 8 : 16}>
              <Text>
                <Text type="secondary">Создано: </Text>
                {task.createdAt}
              </Text>
              {task.startedAt && (
                <Text>
                  <Text type="secondary">Начата: </Text>
                  {task.startedAt}
                </Text>
              )}
              {task.completedAt && (
                <Text>
                  <Text type="secondary">Завершена: </Text>
                  {task.completedAt}
                </Text>
              )}
            </TaskDates>
            <Flex vertical gap={8}>
              <Flex gap={isMobile ? 4 : 8} align="center" wrap>
                <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
                  <Icon name="upload" size={16} />
                  <Text type="secondary">{`Учетная запись ${task.system}:`}</Text>
                </Flex>
                <div
                  style={{
                    flexGrow: 1,
                    flexShrink: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Text
                    strong
                    ellipsis={{ tooltip: task.uploadAccount }}
                    style={{ display: 'block' }}
                  >
                    {task.uploadAccount}
                  </Text>
                </div>
              </Flex>
              <Flex gap={isMobile ? 4 : 8} align="center" wrap>
                <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
                  <Icon name="download" size={16} />
                  <Text type="secondary">Учетная запись ГЛОНАССSoft:</Text>
                </Flex>
                <div
                  style={{
                    flexGrow: 1,
                    flexShrink: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Text
                    strong
                    ellipsis={{ tooltip: task.downloadAccount }}
                    style={{ display: 'block' }}
                  >
                    {task.downloadAccount}
                  </Text>
                </div>
              </Flex>
            </Flex>
            <DownloadBtn
              size="small"
              onClick={() => onDownloadCredentials(task.id)}
            >
              Скачать данные пользователей
            </DownloadBtn>
            <ProgressSection align="center" gap={8}>
              <Text type="secondary">Общий прогресс:</Text>
              <Progress percent={task.progress} size="small" showInfo={false} />
              <ProgressValue strong>{task.progress}%</ProgressValue>
            </ProgressSection>
          </TaskData>
          <StatsCard vertical gap={16}>
            {displayConfig.map(({ key, label }) =>
              renderProgressRow(label, safeStats[key])
            )}
          </StatsCard>
        </Content>

        {showLogs && (
          <>
            <Divider />
            <TaskLogs taskId={task.id} taskStatus={task.status} />
          </>
        )}

        {task.hasErrors && (
          <ErrorAlert
            title={
              <>
                <strong>Ошибка:</strong> Ошибки при выполнении задачи
              </>
            }
            showIcon
          />
        )}
      </SectionCard>
      {showReconfigButton && (
        <ReconfigurationModal
          open={isReconfigModalOpen}
          onClose={handleCloseReconfigModal}
          taskNumber={task.number}
          vehicleIds={task.vehicleIds}
        />
      )}
    </>
  )
}
